import {fCurrency} from "../../utils/formatNumber";
import {getDataForPrintingTicket, isPovlasticaTicket} from "../../helper";
import {getA4Calibration, getSessionSelectedLocation} from "../../helper/session";
import moment from "moment/moment";

const CUT_MM = [72, 147, 222];

export const CALIBRATION_MM = [20, 270];

const CUT_GUTTER_MM = 2.5;
const PAGE_TOP_MM = 5;
const PAGE_BOTTOM_MM = 292;

const MAX_CONTENT_MM = 295;

function place(mm, calibration) {
    return (mm - calibration.topMm) / calibration.scale;
}

function slotBox(slot, calibration) {
    const start = slot === 0 ? PAGE_TOP_MM : CUT_MM[slot - 1] + CUT_GUTTER_MM;
    const end = slot === CUT_MM.length ? PAGE_BOTTOM_MM : CUT_MM[slot] - CUT_GUTTER_MM;
    const top = place(start, calibration);
    const bottom = Math.min(place(end, calibration), MAX_CONTENT_MM);
    return {top, height: Math.max(bottom - top, 0)};
}

const SIDE_MARGIN = "6mm";
const PAGE_HEIGHT = `${MAX_CONTENT_MM + 1}mm`;

const style = `
  @page {
    size: 210mm 297mm;
    margin: 0;
  }
  @media print {
    html, body {
      width: 210mm;
      margin: 0;
      padding: 0;
    }
  }
  html, body {
    width: 210mm;
    max-width: 210mm;
  }
`;

const B = "1px solid #000";
const BT = "1px solid #999";
const ROUTE_BG = "rgba(214, 232, 208, 0.55)";
const FONT = "Arial, Helvetica, sans-serif";

function bl(sr, en) {
    return (<><b>{sr}</b> <span style={{color: "#555"}}>/ {en}</span></>);
}

const TITLE_PREFIX = "AUTOBUSKA KARTA - ";
const TITLE_DRIVER_OUT = `${TITLE_PREFIX}KUPON ZA VOZAČA (POLAZAK)`;
const TITLE_DRIVER_RET = `${TITLE_PREFIX}KUPON ZA VOZAČA (POVRATAK)`;
const TITLE_PASSENGER = `${TITLE_PREFIX}KUPON ZA PUTNIKA`;
const TITLE_CARRIER = `${TITLE_PREFIX}PRIMERAK ZA PREVOZNIKA`;

const DASH = "--------";

// Cena jednog smera: karta + usluge (samo na polaznoj) + akcije tog smera.
function legFare(ticket, services, actionOBJ) {
    if (!ticket) return {total: 0, acts: []};
    const pUid = ticket?.passenger?.passengerUid;
    const acts = Array.isArray(actionOBJ) && pUid
        ? actionOBJ.filter(o => o.passengerUid?.includes(pUid) && o.isReturnTicket === ticket.isReturnTicket)
        : [];
    let total = ticket.price;
    if (services && !ticket.isReturnTicket) {
        total += services.reduce((a, {unitPrice}) => a + unitPrice, 0);
    }
    total += acts.reduce((a, c) => a + (c.unitPrice || 0), 0);
    return {total, acts};
}

// Jedan smer putovanja. Otvorena povratna nema polazak ni liniju - ta polja se pišu rukom.
function buildLeg(ticket, {seatSelectionEnabled, isSelfSale, barcodeDataUrl, arrTripTime}) {
    if (!ticket) return null;
    return {
        ticket,
        data: getDataForPrintingTicket(ticket, {seatSelectionEnabled, isSelfSale}),
        carrierName: ticket.isReturnTicket
            ? (ticket?.outboundTicket?.trip?.line?.operator?.name || "/")
            : (ticket?.trip?.line?.operator?.name || ticket?.operator?.name || ""),
        lineName: ticket.isReturnTicket
            ? (ticket?.outboundTicket?.trip?.line?.name || "/")
            : (ticket?.trip?.line?.name || ""),
        barcodeDataUrl,
        arrTripTime,
        hasSchedule: !ticket.isReturnTicket,
    };
}

function Watermark({show}) {
    if (!show) return null;
    const lines = [[0, 100, 40, 0], [30, 100, 70, 0], [60, 100, 100, 0]];
    return (
        <svg
            viewBox="0 0 100 100" preserveAspectRatio="none"
            style={{
                position: "absolute", top: 0, left: 0, width: "100%", height: "100%",
                zIndex: 0,
            }}>
            {lines.map(([x1, y1, x2, y2], i) => (
                <line
                    key={`wm${i}`}
                    x1={x1} y1={y1} x2={x2} y2={y2}
                    stroke="#d8d8d8" strokeWidth="6" vectorEffect="non-scaling-stroke"/>
            ))}
        </svg>
    );
}

// ==================== SEKCIJA (1/4 A4) ====================
function Section({slot, children, bordered, fillBorder, watermark}) {
    const box = slotBox(slot, getA4Calibration());
    return (
        <div style={{
            position: "absolute",
            top: `${box.top.toFixed(2)}mm`,
            left: SIDE_MARGIN,
            right: SIDE_MARGIN,
            height: `${box.height.toFixed(2)}mm`,
            overflow: "hidden",
            boxSizing: "border-box",
            ...(bordered ? {border: B} : {}),
            ...(fillBorder ? {display: "flex", flexDirection: "column"} : {}),
        }}>
            <Watermark show={watermark}/>
            {children}
        </div>
    );
}

// ==================== PERFORACIJA ====================
function Perforation({slot}) {
    return (
        <div style={{
            position: "absolute",
            top: `${place(CUT_MM[slot], getA4Calibration()).toFixed(2)}mm`,
            left: SIDE_MARGIN,
            right: SIDE_MARGIN,
            borderTop: "1px dashed #666",
            textAlign: "center",
        }}>
            <span style={{
                fontSize: "6pt", color: "#888", backgroundColor: "#fff",
                padding: "0 8px", position: "relative", top: "-6px"
            }}>&#9986;</span>
        </div>
    );
}

function CalibrationMarks() {
    if (!getA4Calibration().marks) {
        return null;
    }
    return (
        <>
            {CALIBRATION_MM.map((mm) => (
                <div key={mm} style={{
                    position: "absolute",
                    top: `${mm}mm`,
                    left: 0,
                    width: "25mm",
                    borderTop: "1px solid #000"
                }}>
                    <span style={{fontSize: "6pt", paddingLeft: "1mm"}}>{mm} mm</span>
                </div>
            ))}
        </>
    );
}

// ==================== KARTA BLOK ====================
function TicketBlock({
                         blockTitle, identity, outLeg, retLeg, fare, showBothIds,
                         services, acts, logo, carrierContact,
                         maxDaysOfReturn, advertisementText
                     }) {
    const {
        ticketId, ticketType, price, cashier, salesTime,
        platformNumber, seat, passengerDetails, passengerDocumentNumber, issuedBy,
    } = identity.data;

    // Povlastica: umesto imena i prezimena štampamo broj povlastice (ime ostaje u bazi zbog izveštaja).
    const isPovlastica = isPovlasticaTicket(identity.ticket);

    // Kupon za putnika i primerak za prevoznika pokrivaju obe karte, pa nose oba broja.
    const bothIds = showBothIds && retLeg
        ? `${outLeg?.data?.ticketId} / ${retLeg.data.ticketId}`
        : null;

    const saleLocation = getSessionSelectedLocation()?.name || "";

    const fs = "7.5pt";
    const fsL = "6.5pt";
    const pad = "2px 5px";

    // Jedan red rute: stanice, vreme i linija jednog smera. Prazan red kad smera nema.
    const routeRow = (leg, highlight) => (
        <tr style={{
            borderBottom: B,
            ...(highlight ? {backgroundColor: ROUTE_BG} : {}),
        }}>
            <td style={{padding: "2px 5px", borderRight: BT}} colSpan={1}>
                <div>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Od", "from")}</div>
                    <div style={{fontWeight: leg ? 700 : 400}}>{leg?.data?.departureStationName || DASH}</div>
                </div>
                <div style={{marginTop: "2px"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Do", "to")}</div>
                    <div style={{fontWeight: leg ? 700 : 400}}>{leg?.data?.arrivalStationName || DASH}</div>
                </div>
            </td>
            <td style={{padding: "2px 5px", borderRight: BT}} colSpan={1}>
                <div>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Polazak", "dep.")}</div>
                    <div style={{fontWeight: 700}}>
                        {leg?.hasSchedule ? `${leg.data.journeyDate} ${leg.data.departureTime}` : DASH}
                    </div>
                </div>
                <div style={{marginTop: "2px"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Dolazak", "arr.")}</div>
                    <div style={{fontWeight: 700}}>
                        {leg?.hasSchedule && leg.arrTripTime
                            ? `${moment.utc(leg.arrTripTime).format("DD.MM.YYYY")} ${moment.utc(leg.arrTripTime).format("HH:mm")}`
                            : DASH}
                    </div>
                </div>
            </td>
            <td style={{padding: "2px 5px"}} colSpan={2}>
                <div style={{fontSize: fsL, color: "#555"}}>{bl("Linija (Prevoznik)", "line (carrier)")}</div>
                <div style={{fontWeight: 700}}>{leg?.hasSchedule ? leg.lineName : DASH}</div>
                <div style={{fontSize: fsL}}>{leg?.carrierName}</div>
            </td>
        </tr>
    );

    return (
        <table style={{
            width: "100%", borderCollapse: "collapse", position: "relative", zIndex: 1,
            fontFamily: FONT, fontSize: fs, tableLayout: "fixed"
        }}>
            <colgroup>
                <col style={{width: "13%"}}/>
                <col style={{width: "20%"}}/>
                <col style={{width: "20%"}}/>
                <col style={{width: "27%"}}/>
                <col style={{width: "20%"}}/>
            </colgroup>
            <tbody>

            {/* RED 1: LOGO+QR (rowspan) | NASLOV | BARCODE */}
            <tr style={{borderBottom: B}}>
                <td rowSpan={6} style={{
                    borderRight: B, padding: "4px", verticalAlign: "top",
                    textAlign: "center"
                }}>
                    {logo && (
                        <div style={{marginBottom: "3px"}}>
                            <img alt="logo" src={logo} style={{maxWidth: "90%", maxHeight: "35px"}}/>
                        </div>
                    )}
                    <div style={{fontSize: fsL, fontWeight: 700, marginBottom: "2px"}}>{outLeg?.carrierName}</div>
                    {carrierContact?.phone && (
                        <div style={{fontSize: "6pt", color: "#555"}}>Tel: {carrierContact.phone}</div>
                    )}
                    {carrierContact?.email && (
                        <div style={{fontSize: "6pt", color: "#555"}}>{carrierContact.email}</div>
                    )}
                    {carrierContact?.webAddress && (
                        <div style={{fontSize: "6pt", color: "#555"}}>{carrierContact.webAddress}</div>
                    )}
                    {identity.ticket.parsedQR && (
                        <div style={{marginTop: "4px"}}>
                            <img alt="qr" src={identity.ticket.parsedQR}
                                 style={{width: "65px", height: "65px"}}/>
                        </div>
                    )}
                    {advertisementText ? (
                        <div style={{fontSize: "5.5pt", color: "#888", marginTop: "3px", borderTop: "1px solid #ddd", paddingTop: "2px"}}>
                            {advertisementText}
                        </div>
                    ) : carrierContact?.platformContact ? (
                        <div style={{fontSize: "5.5pt", color: "#888", marginTop: "3px", borderTop: "1px solid #ddd", paddingTop: "2px"}}>
                            {carrierContact.platformContact}
                        </div>
                    ) : null}
                </td>

                {/* Naslov je duži nego ranije, pa uzima i treću kolonu od barkoda. */}
                <td colSpan={3} style={{padding: "3px 5px", verticalAlign: "middle"}}>
                    <div style={{fontSize: "9pt", fontWeight: 700, letterSpacing: "0.3px"}}>
                        {blockTitle}
                    </div>
                </td>

                <td colSpan={1} style={{padding: "2px 4px", textAlign: "right", verticalAlign: "middle"}}>
                    {identity.barcodeDataUrl && (
                        <div style={{display: "inline-block", textAlign: "center"}}>
                            <img alt="barcode" src={identity.barcodeDataUrl}
                                 style={{height: "20px", maxWidth: "130px"}}/>
                            <div style={{fontSize: "5.5pt"}}>{ticketId}</div>
                        </div>
                    )}
                </td>
            </tr>

            {/* RED 2: Ref.br | Ticket br | "Valid for one person" */}
            <tr style={{borderBottom: B}}>
                <td style={{padding: pad}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Ref. broj", "ref. no.")}</div>
                    <div style={{fontWeight: 700}}>{bothIds || identity.ticket.id || ticketId}</div>
                </td>
                <td style={{padding: pad}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Broj karte", "ticket no.")}</div>
                    <div style={{fontWeight: 700, fontSize: "10pt"}}>{bothIds || ticketId}</div>
                </td>
                <td colSpan={2} style={{padding: pad, fontSize: fsL, color: "#555", verticalAlign: "middle"}}>
                    Karta vazi samo za jednu osobu / ticket valid only for one person
                </td>
            </tr>

            {/* RED 3: Ime putnika (za povlastice: broj povlastice umesto imena) */}
            <tr style={{borderBottom: B}}>
                <td colSpan={4} style={{padding: pad}}>
                    {isPovlastica ? (
                        <>
                            <span style={{fontSize: fsL, color: "#555"}}>{bl("Broj povlastice", "privilege no.")}</span>
                            {" "}
                            <span style={{fontWeight: 700, fontSize: "8.5pt"}}>{passengerDocumentNumber?.trim() || "---"}</span>
                        </>
                    ) : (
                        <>
                            <span style={{fontSize: fsL, color: "#555"}}>{bl("Ime putnika", "name of passenger")}</span>
                            {" "}
                            <span style={{fontWeight: 700, fontSize: "8.5pt"}}>{passengerDetails?.trim() || "---"}</span>
                        </>
                    )}
                </td>
            </tr>

            {/* RED 4: POLAZAK */}
            {routeRow(outLeg, true)}

            {/* RED 5: POVRATAK (prazan kad karta nema povratak - popunjava se rukom) */}
            {routeRow(retLeg, false)}

            {/* RED 6: FOOTER */}
            <tr>
                <td style={{padding: "2px 5px", borderRight: BT, verticalAlign: "top"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Mesto izdavanja", "issuing office")}</div>
                    <div style={{fontWeight: 700}}>{issuedBy}</div>
                    <div style={{fontSize: fsL}}>{saleLocation}</div>
                    <div style={{fontSize: fsL}}>
                        Peron: {identity.hasSchedule ? (platformNumber || "---") : "/"}
                        {" | "}Sediste: {seat !== '' ? (seat || "NN") : "______"}
                    </div>
                </td>
                <td style={{padding: "2px 5px", borderRight: BT, verticalAlign: "top"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Izdato", "issued")}</div>
                    <div style={{fontWeight: 700}}>{salesTime}</div>
                    <div style={{fontSize: fsL}}>
                        Operater: <b>{cashier}</b>
                    </div>
                </td>
                <td style={{padding: "2px 5px", borderRight: BT, verticalAlign: "top"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Napomene", "notes")}</div>
                    <div style={{fontSize: fsL}}>
                        {retLeg && (
                            <div style={{fontWeight: 700}}>
                                Povratna karta vazi {maxDaysOfReturn || "NN"} dana uz kartu br. {outLeg?.data?.ticketId}
                            </div>
                        )}
                        {services?.map((item, i) => (
                            <span key={`s${i}`}>{item.name}: {fCurrency(`${item.unitPrice}`)}; </span>
                        ))}
                        {acts?.map((obj, i) => (
                            <span key={`a${i}`}>{obj.name}: {fCurrency(`${obj.unitPrice}`)}; </span>
                        ))}
                    </div>
                </td>
                <td style={{padding: "2px 5px", verticalAlign: "top"}}>
                    <div>
                        <div style={{fontSize: fsL, color: "#555"}}>{bl("Ukupna cena", "total fare")}</div>
                        <div style={{fontWeight: 700, fontSize: "10pt", textAlign: "right"}}>
                            {fare ? fCurrency(`${fare.total}`) : price}
                        </div>
                        {fare?.ret != null && (
                            <div style={{fontSize: fsL, color: "#555", textAlign: "right"}}>
                                (polazak {fCurrency(`${fare.out}`)} + povratak {fCurrency(`${fare.ret}`)})
                            </div>
                        )}
                    </div>
                    <div style={{borderTop: "1px solid #ccc", paddingTop: "1px"}}>
                        <div style={{fontSize: fsL, color: "#555"}}>{bl("Tarifa", "tariff")}</div>
                        <div style={{fontWeight: 600}}>{ticketType}</div>
                    </div>
                </td>
            </tr>
            </tbody>
        </table>
    );
}

// ==================== KUPON BLOK ====================
function CouponBlock({ticket, ticketData, carrierName}) {
    const {
        ticketId, departureStationName, arrivalStationName,
        ticketType, journeyDate, departureTime, price, issuedBy,
        passengerDocumentNumber,
    } = ticketData;

    const isPovlastica = isPovlasticaTicket(ticket);

    const saleLocation = getSessionSelectedLocation()?.name || "";
    const fs = "7.5pt";
    const fsL = "6.5pt";
    const pad = "2px 5px";

    return (
        <table style={{
            width: "100%", height: "100%", borderCollapse: "collapse",
            fontFamily: FONT, fontSize: fs, tableLayout: "fixed"
        }}>
            <tbody>
            <tr style={{borderBottom: B, backgroundColor: "#f5f5f5"}}>
                <td style={{padding: "3px 5px", fontWeight: 700, fontSize: "9pt", textAlign: "center"}} colSpan={4}>
                    KUPON ZA PREVOZNIKA / carrier coupon
                </td>
            </tr>
            <tr style={{borderBottom: BT}}>
                <td style={{padding: pad, width: "25%", borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Broj karte", "ticket no.")}</div>
                    <div style={{fontWeight: 700}}>{ticketId}</div>
                </td>
                <td style={{padding: pad, width: "25%", borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Prevoznik", "carrier")}</div>
                    <div style={{fontWeight: 700}}>{carrierName}</div>
                </td>
                <td style={{padding: pad, width: "25%", borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Izdao", "issued by")}</div>
                    <div style={{fontWeight: 700}}>{issuedBy}</div>
                    <div style={{fontSize: fsL}}>{saleLocation}</div>
                </td>
                <td style={{padding: pad, width: "25%"}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Vrsta karte", "type")}</div>
                    <div style={{fontWeight: 700}}>{ticketType}</div>
                </td>
            </tr>
            <tr>
                <td style={{padding: pad, borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Od", "from")}</div>
                    <div style={{fontWeight: 700}}>{departureStationName}</div>
                </td>
                <td style={{padding: pad, borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Do", "to")}</div>
                    <div style={{fontWeight: 700}}>{arrivalStationName}</div>
                </td>
                <td style={{padding: pad, borderRight: BT}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Datum i vreme", "date & time")}</div>
                    <div style={{fontWeight: 700}}>
                        {ticket?.isReturnTicket ? "--------" : `${journeyDate} ${departureTime}`}
                    </div>
                </td>
                <td style={{padding: pad}}>
                    <div style={{fontSize: fsL, color: "#555"}}>{bl("Cena", "price")}</div>
                    <div style={{fontWeight: 700, fontSize: "9pt"}}>{price}</div>
                </td>
            </tr>
            {isPovlastica && passengerDocumentNumber?.trim()?.length > 0 && (
                <tr style={{borderTop: BT}}>
                    <td colSpan={4} style={{padding: pad}}>
                        <span style={{fontSize: fsL, color: "#555"}}>{bl("Broj povlastice", "privilege no.")}</span>
                        {" "}
                        <span style={{fontWeight: 700}}>{passengerDocumentNumber.trim()}</span>
                    </td>
                </tr>
            )}
            </tbody>
        </table>
    );
}

// ==================== GLAVNI EXPORT: dva formata ====================

// Jednosmerna: 3/4 A4 - kupon za vozača, kupon za putnika, primerak za prevoznika.
// Četvrti odsečak ostaje prazan; drugi red rute je prazan i popunjava se rukom.
export default function A4TicketSlip(ticket, services, logo, seatSelectionEnabled, isSelfSale, actionOBJ, barcodeDataUrl, arrTripTime, maxDaysOfReturn, carrierContact, advertisementText) {
    const outLeg = buildLeg(ticket, {seatSelectionEnabled, isSelfSale, barcodeDataUrl, arrTripTime});
    const out = legFare(ticket, services, actionOBJ);
    const fare = {out: out.total, ret: null, total: out.total};

    const common = {
        identity: outLeg, outLeg, retLeg: null, fare,
        services, acts: out.acts, logo, carrierContact, maxDaysOfReturn, advertisementText,
    };

    return (
        <html lang="sr">
        <head>
            <meta charSet="UTF-8"/>
            <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8"/>
            <style type="text/css">{style}</style>
            <title>A4 Karta {outLeg.data.ticketId}</title>
        </head>
        <body style={{
            fontFamily: FONT, margin: "0", padding: "0",
            boxSizing: "border-box", position: "relative",
            width: "210mm", maxWidth: "210mm", height: PAGE_HEIGHT
        }}>

        <CalibrationMarks/>

        <Section slot={0} bordered>
            <TicketBlock {...common} blockTitle={TITLE_DRIVER_OUT}/>
        </Section>

        <Perforation slot={0}/>

        <Section slot={1} bordered watermark>
            <TicketBlock {...common} blockTitle={TITLE_PASSENGER}/>
        </Section>

        <Perforation slot={1}/>

        <Section slot={2} bordered>
            <TicketBlock {...common} blockTitle={TITLE_CARRIER}/>
        </Section>

        <Perforation slot={2}/>

        </body>
        </html>
    );
}

// Povratna: ceo A4 na 4 odsečka. Svi nose oba smera i ukupnu cenu; razlikuju se po naslovu,
// a kupon za vozača povratka nosi broj i QR povratne karte da može da se skenira zasebno.
export function A4TicketSlipDual(
    outboundTicket, returnTicket, services, logo,
    seatSelectionEnabled, isSelfSale, actionOBJ,
    outboundBarcode, returnBarcode, arrTripTime,
    maxDaysOfReturn, carrierContact, confirmation, advertisementText
) {
    const outLeg = buildLeg(outboundTicket, {
        seatSelectionEnabled, isSelfSale, barcodeDataUrl: outboundBarcode, arrTripTime
    });
    const retLeg = buildLeg(returnTicket, {
        seatSelectionEnabled, isSelfSale, barcodeDataUrl: returnBarcode, arrTripTime: null
    });

    const out = legFare(outboundTicket, services, actionOBJ);
    const ret = legFare(returnTicket, services, actionOBJ);
    const fare = {out: out.total, ret: ret.total, total: out.total + ret.total};

    const common = {
        outLeg, retLeg, fare,
        services, acts: [...out.acts, ...ret.acts],
        logo, carrierContact, maxDaysOfReturn, advertisementText,
    };

    return (
        <html lang="sr">
        <head>
            <meta charSet="UTF-8"/>
            <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8"/>
            <style type="text/css">{style}</style>
            <title>A4 Karta {outLeg.data.ticketId}</title>
        </head>
        <body style={{
            fontFamily: FONT, margin: "0", padding: "0",
            boxSizing: "border-box", position: "relative",
            width: "210mm", maxWidth: "210mm", height: PAGE_HEIGHT
        }}>

        <CalibrationMarks/>

        <Section slot={0} bordered>
            <TicketBlock {...common} identity={outLeg} blockTitle={TITLE_DRIVER_OUT}/>
        </Section>

        <Perforation slot={0}/>

        <Section slot={1} bordered>
            <TicketBlock {...common} identity={retLeg} blockTitle={TITLE_DRIVER_RET}/>
        </Section>

        <Perforation slot={1}/>

        <Section slot={2} bordered watermark>
            <TicketBlock {...common} identity={outLeg} blockTitle={TITLE_PASSENGER} showBothIds/>
        </Section>

        <Perforation slot={2}/>

        <Section slot={3} bordered>
            <TicketBlock {...common} identity={outLeg} blockTitle={TITLE_CARRIER} showBothIds/>
        </Section>

        </body>
        </html>
    );
}

// Format kupona na A4 (za parove: jedan smer + povratna).
export function A4CouponSlipDual(outboundTicket, returnTicket, services, logo, seatSelectionEnabled, isSelfSale) {
    const outData = getDataForPrintingTicket(outboundTicket, {seatSelectionEnabled, isSelfSale});
    const retData = getDataForPrintingTicket(returnTicket, {seatSelectionEnabled, isSelfSale});

    const outCarrier = outboundTicket?.trip?.line?.operator?.name || outboundTicket?.operator?.name || "";
    const retCarrier = returnTicket?.outboundTicket?.trip?.line?.operator?.name || "/";

    return (
        <html lang="sr">
        <head>
            <meta charSet="UTF-8"/>
            <meta httpEquiv="Content-Type" content="text/html; charset=UTF-8"/>
            <style type="text/css">{style}</style>
            <title>A4 Kuponi</title>
        </head>
        <body style={{
            fontFamily: FONT, margin: "0",
            padding: "6mm", boxSizing: "border-box",
            width: "210mm", maxWidth: "210mm"
        }}>

        <CouponBlock ticket={outboundTicket} ticketData={outData} carrierName={outCarrier}/>

        <div style={{margin: "3mm 0"}}/>

        <CouponBlock ticket={returnTicket} ticketData={retData} carrierName={retCarrier}/>

        </body>
        </html>
    );
}
